// useTawDealLookup({ dealId, tawIds, taws, enabled }) -- the team-deal loaded
// signal for the /transactions/<id> route (Phase 55 review follow-up to
// S-WR-01).
//
// The problem it solves: a team (TAW) parent owner who opens a member's deal
// from a pasted link, a new tab, or a fresh session has no route state, and
// under the default team scope 'own' useTawTransactions acquires ZERO team
// slices, so the store holds no team rows at all. Without a signal that the
// team read has finished, the route could only wait forever (or, before the
// first review fix, call a real deal "not found").
//
// What it does: a ONE-SHOT getDoc of `workspaces/<tawId>/transactions/<dealId>`
// in every team agent workspace the parent lists (the TW doc's own
// teamAgentWorkspaceIds, available at once from the boot workspaces listener,
// so the lookup never races the useTaws roster load). `loaded` turns true
// once every read has settled; `deal` is the member's deal, enriched exactly
// like a useTawTransactions row (enrichTawRows), or null when no workspace
// holds the id.
//
// Why a by-id read rather than widening the team listeners: it opens NO
// listener (the boot listener census is untouched, and nothing runs unless a
// parent owner is on a deal route the own slice cannot resolve), it finds a
// member's deal in ANY archive year (the team listeners are windowed, active
// by default, so an archived member deal would read as missing), and it costs
// one read per team agent workspace per deal visit.
//
// A read that fails (for example permission-denied for one workspace) is
// reported through reportTawSliceError and counts as "not in that workspace",
// so one bad workspace cannot leave the route loading forever. A migrated or
// hidden deal is dropped (dropMigratedDeals), the same as the team listeners.
//
// The answer is keyed on (dealId, the sorted workspace ids): a new id or a
// changed roster drops the previous answer until its own reads settle, so an
// old deal never answers for a new URL.
import React from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase.js';
import { dropMigratedDeals } from '../../utils/migratedDeals.js';
import { enrichTawRows, reportTawSliceError } from './useTawTransactions.js';

const NOT_LOADED = Object.freeze({ deal: null, loaded: false });
const LOADED_EMPTY = Object.freeze({ deal: null, loaded: true });

/**
 * Reads `dealId` in each team agent workspace and resolves to the first
 * `{ tawId, row }` that holds a non-migrated deal, or null. Never rejects.
 */
export async function lookupTawDeal(dealId, tawIds, onError = reportTawSliceError) {
    const results = await Promise.all((tawIds || []).map(async (tawId) => {
        try {
            const snap = await getDoc(doc(db, 'workspaces', tawId, 'transactions', dealId));
            if (!snap || typeof snap.exists !== 'function' || !snap.exists()) return null;
            const [row] = dropMigratedDeals([{ ...snap.data(), id: snap.id ?? dealId }]);
            return row ? { tawId, row } : null;
        } catch (error) {
            onError(tawId, error);
            return null;
        }
    }));
    return results.find(Boolean) ?? null;
}

export function useTawDealLookup({ dealId, tawIds, taws, enabled = true } = {}) {
    const idsKey = Array.isArray(tawIds) ? [...tawIds].sort().join(',') : '';
    const active = Boolean(enabled && dealId);
    const requestKey = active ? `${dealId}|${idsKey}` : null;

    const [result, setResult] = React.useState({ key: null, found: null });

    React.useEffect(() => {
        if (!requestKey || !idsKey) return undefined;
        let cancelled = false;
        lookupTawDeal(dealId, idsKey.split(',')).then((found) => {
            if (!cancelled) setResult({ key: requestKey, found });
        });
        return () => { cancelled = true; };
        // requestKey carries dealId and idsKey by value.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestKey]);

    const settled = requestKey !== null && result.key === requestKey;
    const found = settled ? result.found : null;
    const taw = found ? (taws || []).find((t) => t.id === found.tawId) : undefined;
    const deal = React.useMemo(
        () => (found ? enrichTawRows([found.row], found.tawId, taw)[0] : null),
        [found, taw],
    );
    const answer = React.useMemo(() => ({ deal, loaded: true }), [deal]);

    if (!active) return NOT_LOADED;
    // A team with no agent workspaces holds no member deals: loaded at once.
    if (!idsKey) return LOADED_EMPTY;
    return settled ? answer : NOT_LOADED;
}
